/**
 * Result shape returned by simplified mock paging/filtering/sorting.
 */
export interface MockPagingResult {
  data: Array<Record<string, unknown>>;
  totalRecords: number;
  pageNumber: number;
  pageSize: number;
  serverSidePaging: true;
}

/**
 * Applies simple query-param filtering/sorting/paging to an array dataset.
 */
export function getSimpleMockPagingResult(url: URL, dataset: unknown): MockPagingResult {
  const pageNumber = parsePositiveInt(url.searchParams.get("pageNumber"), 1);
  const pageSize = parsePositiveInt(url.searchParams.get("pageSize"), 20);
  const sortBy = url.searchParams.get("sortBy")?.trim() || "";
  const sortOrder = normalizeSortOrder(url.searchParams.get("sortOrder"));
  const filters = getFilterEntries(url.searchParams);

  const rows = Array.isArray(dataset) ? [...dataset as Array<Record<string, unknown>>] : [];
  const filteredData = applySimpleFilters(rows, filters);
  const sortedData = applySimpleSort(filteredData, sortBy, sortOrder);

  return {
    data: applySimplePaging(sortedData, pageNumber, pageSize),
    totalRecords: sortedData.length,
    pageNumber,
    pageSize,
    serverSidePaging: true,
  };
}

/**
 * Parses a positive integer query parameter with a fallback.
 */
function parsePositiveInt(value: string | null, fallback: number): number {
  const parsed = Number.parseInt(value ?? "", 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/**
 * Normalizes sort direction to `asc` or `desc`.
 */
function normalizeSortOrder(value: string | null): "asc" | "desc" {
  return value?.toLowerCase() === "desc" ? "desc" : "asc";
}

/**
 * Gets non-reserved query params to use as simple filter entries.
 */
function getFilterEntries(params: URLSearchParams): Array<[string, string]> {
  const reservedKeys = new Set(["pageNumber", "pageSize", "sortBy", "sortOrder"]);
  const entries: Array<[string, string]> = [];

  params.forEach((value, key) => {
    if (reservedKeys.has(key)) {
      return;
    }
    const trimmed = value.trim();
    if (!trimmed.length) {
      return;
    }
    entries.push([key, trimmed]);
  });

  return entries;
}

/**
 * Applies case-insensitive string contains, and exact number/boolean filters.
 */
function applySimpleFilters(rows: Array<Record<string, unknown>>, filters: Array<[string, string]>): Array<Record<string, unknown>> {
  if (!filters.length) {
    return rows;
  }

  return rows.filter((row) => {
    return filters.every(([field, rawFilterValue]) => {
      const rowValue = row[field];
      if (rowValue === null || rowValue === undefined) {
        return false;
      }

      if (typeof rowValue === "string") {
        return rowValue.toLowerCase().includes(rawFilterValue.toLowerCase());
      }

      if (typeof rowValue === "number") {
        const parsed = Number(rawFilterValue);
        return Number.isFinite(parsed) && rowValue === parsed;
      }

      if (typeof rowValue === "boolean") {
        const boolFilter = parseBoolean(rawFilterValue);
        return boolFilter !== null && rowValue === boolFilter;
      }

      return String(rowValue).toLowerCase().includes(rawFilterValue.toLowerCase());
    });
  });
}

/**
 * Applies field-based sorting if a valid sort field is provided.
 */
function applySimpleSort(rows: Array<Record<string, unknown>>, sortBy: string, sortOrder: "asc" | "desc"): Array<Record<string, unknown>> {
  if (!sortBy) {
    return rows;
  }

  return [...rows].sort((a, b) => {
    const aVal = a[sortBy];
    const bVal = b[sortBy];
    const cmp = compareUnknown(aVal, bVal);
    return sortOrder === "desc" ? -cmp : cmp;
  });
}

/**
 * Slices the input rows using pageNumber/pageSize.
 */
function applySimplePaging(rows: Array<Record<string, unknown>>, pageNumber: number, pageSize: number): Array<Record<string, unknown>> {
  const start = (pageNumber - 1) * pageSize;
  const end = start + pageSize;
  return rows.slice(start, end);
}

/**
 * Compares unknown values for sorting.
 */
function compareUnknown(a: unknown, b: unknown): number {
  if (a === b) {
    return 0;
  }
  if (a === null || a === undefined) {
    return 1;
  }
  if (b === null || b === undefined) {
    return -1;
  }

  if (typeof a === "string" && typeof b === "string") {
    return a.localeCompare(b, undefined, { sensitivity: "base" });
  }
  if (typeof a === "number" && typeof b === "number") {
    return a - b;
  }
  if (typeof a === "boolean" && typeof b === "boolean") {
    return Number(a) - Number(b);
  }

  return String(a).localeCompare(String(b), undefined, { sensitivity: "base" });
}

/**
 * Parses a boolean from common string values.
 */
function parseBoolean(value: string): boolean | null {
  const normalized = value.trim().toLowerCase();
  if (normalized === "true") {
    return true;
  }
  if (normalized === "false") {
    return false;
  }
  return null;
}
